/**
 * Session and profile requests: who is signed in, the CSRF token that
 * comes with the answer, sign-out, the identity providers on offer, the
 * workspace profile and onboarding completion.
 */

import { ApiFailure, rememberCsrfToken, request } from './request.js';

export interface SessionAccount {
  readonly id: string;
  readonly displayName: string;
  readonly role: string;
}

export interface SessionInfo {
  readonly authenticated: true;
  readonly account: SessionAccount;
  readonly csrfToken: string;
}

export interface WorkspaceProfile {
  readonly visibleTaskTypes: readonly string[];
  readonly mayAdministerTasks: boolean;
  readonly mayDeleteDataRoomFolders: boolean;
  readonly mayUseDataRoom: boolean;
  readonly onboardingCompleted: boolean;
}

/** `null` means "not signed in" — the one 401 the app treats as an answer. */
export async function fetchSession(): Promise<SessionInfo | null> {
  try {
    const session = await request<SessionInfo>('GET', '/auth/session');
    rememberCsrfToken(session.csrfToken);
    return session;
  } catch (failure) {
    if (failure instanceof ApiFailure && failure.kind === 'unauthenticated') {
      rememberCsrfToken(null);
      return null;
    }
    throw failure;
  }
}

export async function logout(): Promise<void> {
  await request<unknown>('POST', '/auth/logout');
  rememberCsrfToken(null);
}

export function fetchProviders(): Promise<{ providers: readonly string[] }> {
  return request('GET', '/auth/providers');
}

export function fetchProfile(): Promise<WorkspaceProfile> {
  return request('GET', '/workspace/profile');
}

export async function completeOnboarding(): Promise<void> {
  await request<unknown>('POST', '/workspace/onboarding/complete');
}
